import React from 'react';
import {
  Link,
  getRouteApi,
  useLoaderData,
} from '@tanstack/react-router';

import DashboardTimelineCard from './TimelineCard';
import Pagination from '../Pagination';
import PaginationHeader from '../PaginationHeader';

import type { Timeline } from '../../types/timeline';

interface DashboardTimelinesProps {
  style?: 'default' | 'short';
};

const DashboardTimelines: React.FC<DashboardTimelinesProps> = ({ style = 'default' }) => {
  let user;
  try {
    const routeApi = getRouteApi('/dashboard/admin/users/$userId');
    const { user: loadedUser } = routeApi.useLoaderData();
    user = loadedUser;
  } catch (error) {
    if (error instanceof Error) {
      user = undefined;
    }
  }

  const { timelines: timelinesData } = useLoaderData({ strict: false });
  const timelines = timelinesData?.data || [];
  const totalTimelines = timelinesData?.meta.total;
  const mode = timelinesData?.meta.mode || 'created';

  return (
    <>
      {style !== 'short' && (
        <div className="dashboard-header page-header">
          {mode === 'received' && (
            <>
              <h2 className="page-title">Received <em>Timelines</em></h2>
              <p>All Timelines that have been shared with you.</p>
            </>
          )}
          {mode === 'contributing' && (
            <>
              <h2 className="page-title">My <em>Timelines</em></h2>
              <p>All Timelines that you are contributing to.</p>
            </>
          )}
          {mode === 'created' && (
            <>
              {user ? (
                <>
                  <h2 className="page-title">User’s <em>Timelines</em></h2>
                  <p>Viewing Timelines for {user.name} ({user.email})</p>
                </>
              ) : (
                <>
                  <h2 className="page-title">My <em>Timelines</em></h2>
                  <p>The stories and events you’re preserving, one memory at a time.</p>
                </>
              )}
            </>
          )}
        </div>
      )}
      <div className="dashboard-timelines wa-stack wa-gap-0 inner">
        <div className="wa-grid">
          {(timelines.length > 0 || style === 'short') && (
            <div className="page-eyebrow">
              Timelines
            </div>
          )}
          {totalTimelines > 0 && (
            <div className="total-items">
              {style === 'short' ? (
                <Link to="/dashboard/timelines">
                  View all Timelines ({totalTimelines})
                </Link>
              ) : (
                <PaginationHeader text="Timeline" total={totalTimelines} />
              )}
            </div>
          )}
        </div>
        {timelines.length === 0 && (
          <div className="empty-state wa-stack wa-gap-md">
            <div className="center">
              {user ? (
                <>
                  This user hasn't created any Timelines yet.
                </>
              ) : (
                <>
                  {mode === 'received' && (
                    "You haven't received any Timelines yet."
                  )}
                  {mode === 'contributing' && (
                    "You are not contributing to any Timelines yet."
                  )}
                  {mode === 'created' && (
                    "You don't have any Timelines yet. Create one!"
                  )}
                </>
              )}
            </div>
            {!user && (
              <div className="center">
                {mode === 'created' && (
                  <Link to="/timelines/create">
                    <wa-button className="cta-button">
                      Create Timeline
                    </wa-button>
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
        <div className="dashboard-timelines-list wa-grid" style={{ '--min-column-size': '40ch' } as React.CSSProperties}>
          {timelines.map((timeline: Timeline) => (
            <DashboardTimelineCard key={timeline.id} timeline={timeline} />
          ))}
        </div>
        {style !== 'short' && (
          <Pagination total={timelinesData?.meta.total} />
        )}
      </div>
    </>
  );
}

export default DashboardTimelines;
